import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import GUI from 'lil-gui'

/**
 * Text → 3D Points
 */
function getTextPoints(text, density = 0.1, thickness = 0.2) {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    canvas.width = 800
    canvas.height = 300

    ctx.fillStyle = 'white'
    ctx.font = 'bold 200px Arial'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(text, canvas.width / 2, canvas.height / 2)

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height).data
    const points = []

    for (let y = 0; y < canvas.height; y += 2) {
        for (let x = 0; x < canvas.width; x += 2) {
            const i = (y * canvas.width + x) * 4
            if (imageData[i + 3] > 128 && Math.random() < density) {
                points.push({
                    x: (x - canvas.width / 2) / 100,
                    y: -(y - canvas.height / 2) / 100,
                    z: (Math.random() - 0.5) * thickness
                })
            }
        }
    }

    return points
}

// Слово и толщина
const wordPoints = getTextPoints("AVADA", 0.12, 0.2)

/**
 * Base
 */
const gui = new GUI()
const canvas = document.querySelector('canvas.webgl')
const scene = new THREE.Scene()

const parameters = {}
parameters.count = 10000
parameters.size = 0.01
parameters.radius = 5
parameters.branches = 3
parameters.spin = 1
parameters.randomnes = 0.5
parameters.randomnesPower = 3
parameters.insideColor = '#ff6030'
parameters.outsideColor = '#1b3984'

let geometry = null
let material = null
let points = null
let originalPositions = []
let originalColors = []

const generateGalaxy = () => {
    if (points !== null) {
        geometry.dispose()
        material.dispose()
        scene.remove(points)
    }

    geometry = new THREE.BufferGeometry()
    const positions = new Float32Array(parameters.count * 3)
    const colors = new Float32Array(parameters.count * 3)

    originalPositions = []
    originalColors = []

    const colorInside = new THREE.Color(parameters.insideColor)
    const colorOutside = new THREE.Color(parameters.outsideColor)

    for (let i = 0; i < parameters.count; i++) {
        const i3 = i * 3

        const radius = Math.random() * parameters.radius
        const spinAngle = radius * parameters.spin
        const branchesAngle = (i % parameters.branches) / parameters.branches * Math.PI * 2

        const randomX = Math.pow(Math.random(), parameters.randomnesPower) * (Math.random() < 0.5 ? 1 : -1)
        const randomY = Math.pow(Math.random(), parameters.randomnesPower) * (Math.random() < 0.5 ? 1 : -1)
        const randomZ = Math.pow(Math.random(), parameters.randomnesPower) * (Math.random() < 0.5 ? 1 : -1)

        const x = Math.cos(branchesAngle + spinAngle) * radius + randomX
        const y = randomY
        const z = Math.sin(branchesAngle + spinAngle) * radius + randomZ

        positions[i3] = x
        positions[i3 + 1] = y
        positions[i3 + 2] = z

        originalPositions.push({ x, y, z })

        const mixedColor = colorInside.clone().lerp(colorOutside, radius / parameters.radius)
        colors[i3] = mixedColor.r
        colors[i3 + 1] = mixedColor.g
        colors[i3 + 2] = mixedColor.b

        originalColors.push({ r: mixedColor.r, g: mixedColor.g, b: mixedColor.b })
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3))

    material = new THREE.PointsMaterial({
        size: parameters.size,
        sizeAttenuation: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        vertexColors: true
    })

    points = new THREE.Points(geometry, material)
    scene.add(points)
}

generateGalaxy()

gui.add(parameters, 'count', 0, 1000000, 1000).onChange(generateGalaxy)
gui.add(parameters, 'size', 0, 0.1, 0.001).onChange(generateGalaxy)
gui.add(parameters, 'radius', 0.01, 20, 0.1).onChange(generateGalaxy)
gui.add(parameters, 'branches', 2, 20, 1).onChange(generateGalaxy)
gui.add(parameters, 'spin', -5, 5, 0.001).onChange(generateGalaxy)
gui.add(parameters, 'randomnes', 0.1, 1, 0.001).onChange(generateGalaxy)
gui.add(parameters, 'randomnesPower', 3, 8, 0.001).onChange(generateGalaxy)
gui.addColor(parameters, 'insideColor').onChange(generateGalaxy)
gui.addColor(parameters, 'outsideColor').onChange(generateGalaxy)

/**
 * Sizes
 */
const sizes = { width: window.innerWidth, height: window.innerHeight }
window.addEventListener('resize', () => {
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
})

/**
 * Camera
 */
const camera = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 0.1, 100)
camera.position.set(3, 3, 3)
scene.add(camera)
const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({ canvas })
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

/**
 * Animation
 */
let transformProgress = 0
let transformToWord = false
let transformBack = false


function startMorphCycle() {
    // через 3 сек — в слово
    setTimeout(() => {
        transformToWord = true
        transformBack = false
        transformProgress = 0.001
    }, 5000)

    // через 8 сек — обратно в галактику
    setTimeout(() => {
        transformBack = true
        transformToWord = false
        transformProgress = 0.001
    }, 10000)

    // снова повторить цикл через 8 сек
    setTimeout(startMorphCycle, 10000)
}

// запуск
startMorphCycle()

const clock = new THREE.Clock()

const tick = () => {
    const elapsedTime = clock.getElapsedTime()

    if (points) points.rotation.y = elapsedTime * 0.05

    if (transformToWord || transformBack) {
        transformProgress = Math.min(transformProgress + 0.01, 1)
        const pos = points.geometry.attributes.position.array
        const col = points.geometry.attributes.color.array

        for (let i = 0; i < parameters.count; i++) {
            const i3 = i * 3
            const start = originalPositions[i]
            const target = transformToWord ? wordPoints[i % wordPoints.length] : start

            const from = transformBack ? wordPoints[i % wordPoints.length] : start
            const to = transformBack ? start : target

            pos[i3] = THREE.MathUtils.lerp(from.x, to.x, transformProgress)
            pos[i3 + 1] = THREE.MathUtils.lerp(from.y, to.y, transformProgress)
            pos[i3 + 2] = THREE.MathUtils.lerp(from.z, to.z, transformProgress)

            const origColor = originalColors[i]
            col[i3] = origColor.r
            col[i3 + 1] = origColor.g
            col[i3 + 2] = origColor.b
        }

        points.geometry.attributes.position.needsUpdate = true
        points.geometry.attributes.color.needsUpdate = true

        if (transformProgress >= 1) {
            transformToWord = false
            transformBack = false
        }
    }

    controls.update()
    renderer.render(scene, camera)
    requestAnimationFrame(tick)
}

tick()
